/* eslint-disable jsx-a11y/label-has-associated-control */

import React, {
  FC,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FieldConfig, useField, useFormikContext, Field } from 'formik';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import Input from '../Input';
import clsx from 'clsx';

export interface FormikInputProps
  extends FieldConfig,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'name'> {
  placeholder?: string;
  onInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  getError?: (e: any) => void;
  setError?: (e: any) => void;
  type?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  useCurrencyMask?: boolean;
  usePhoneMask?: boolean;
  useBankMask?: boolean;
  stepName?: string;
  disabled?: boolean;
  hidden?: boolean;
  value?: string | ReadonlyArray<string> | number;
  subOrSupScriptPlaceholder?: string | JSX.Element | null;
  shouldAutoUpdate?: boolean;
  updateCallback?: (...args: any) => Promise<any>;
  refreshCallback?: (...args: any) => Promise<any> | any;
  children?: any;
  innerRef?: any;
  focusTooltip?: string;
  className?: string;
  bgClass?: string;
}

export function FormikInput<T>({
  type,
  placeholder,
  stepName,
  disabled,
  hidden,
  subOrSupScriptPlaceholder,
  onBlur,
  onInput,
  onFocus,
  getError,
  setError,
  shouldAutoUpdate,
  updateCallback,
  refreshCallback,
  innerRef,
  useCurrencyMask,
  usePhoneMask,
  useBankMask,
  focusTooltip,
  className,
  bgClass,
  ...props
}: FormikInputProps): any {
  const [field, meta, helpers] = useField(props.name);
  const {
    handleBlur,
    validateField,
    errors,
    isSubmitting,
    setFieldValue,
    setFieldError,
  } = useFormikContext<T>();
  const [colorInputBlueState, setColorBlueState] = useState({});
  const [colorInputGreenState, setColorGreenState] = useState({});
  const [colorInputRedState, setColorRedState] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOnBlur, setIsOnBlur] = useState(false);
  const { setValue } = helpers;
  const { initialValue, value, error } = meta;
  const { name } = field;
  const nameRef = useRef(name);
  const inputReference = innerRef || nameRef;
  const [currentErrors, setCurrentErrors] = useState<any>(null);
  const [showAxErros, setShowAxerrors] = useState(false);
  const [axErros, setAxerrors] = useState('');
  const [newVal, setNewVal] = useState('');
  const [canCheck, setCanCheck] = useState(false);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (value !== initialValue && canCheck) {
      setColorBlueState({ [name]: name });
    } else {
      setColorBlueState({});
      setIsOnBlur(false);
    }
    return () => {
      setColorGreenState({});
    };
  }, [initialValue, value, isOnBlur, canCheck, name]);

  useEffect(() => {
    if (currentErrors) {
      const namesError = Object.keys(currentErrors);
      const nameError = namesError[0];
      if (meta.touched && meta.error && nameError === name && currentErrors) {
        (inputReference.current as unknown as HTMLInputElement).focus();
        setCurrentErrors(null);
      }
    }
  }, [currentErrors, inputReference, meta.error, meta.touched, name]);

  useEffect(() => {
    if (isSubmitting) setCurrentErrors(errors);
  }, [isSubmitting, meta.touched, meta.error, errors]);

  const processOnBlur = async () => {
    let updateResponse = null;
    const refreshResponse = null;
    setColorRedState({});
    let updateObject;
    if (!error) {
      const oldVal: string = initialValue;
      let newVal: string | null = value;
      if (newVal !== oldVal) {
        if (newVal === '') {
          newVal = null;
        }
        updateObject = { [name]: newVal };
        try {
          setIsUpdating(true);
          if (updateCallback) {
            updateResponse = await updateCallback(updateObject);
            setIsUpdating(false);
            setColorGreenState({ [name]: true });
          }
          setColorBlueState({});
          setColorGreenState({});
          setColorRedState({});
          return { updateResponse, refreshResponse };
        } catch (err: any) {
          setIsUpdating(false);
          setIsOnBlur(false);
          validateField(name);
          setColorRedState({ [name]: true });
          setAxerrors(err?.data?.message || 'error occured');
          setShowAxerrors(true);
          if (getError) getError(err);
        }
      }
    }
    return { updateResponse, refreshResponse };
  };

  useEffect(() => {
    if (!isEmpty(colorInputBlueState) && shouldAutoUpdate && isOnBlur) {
      processOnBlur();
    }
  }, [shouldAutoUpdate, colorInputBlueState]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateField(name);
    setAxerrors('');
    setShowAxerrors(false);
    setColorRedState({});
    if (useCurrencyMask) setNewVal(e.target.value.split(' ').join(''));
    else if (useBankMask || usePhoneMask)
      setNewVal(e.target.value.split(' ').join(''));
    else setNewVal(e.target.value);
  };

  useEffect(() => {
    setTimeout(() => {
      const value =
        type?.toLowerCase() === 'number' && !newVal && +newVal !== 0
          ? undefined
          : newVal;
      setFieldValue(name, value);
    }, 10);
  }, [name, newVal, setFieldValue]);

  useEffect(() => {
    if (initialValue === null || initialValue === undefined) {
      setNewVal('');
      setFieldValue(name, null);
    } else {
      setNewVal(initialValue);
    }
  }, [initialValue, name, setFieldValue]);

  //onblur check if input is number and remove all 0 digit from the left
  const removeZeroDigit = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'number') {
      const value = e.target.value.split(' ').join('');
      if (value && !isNaN(Number(value))) {
        setNewVal(Number(value).toString());
      }
    }
  };

  return (
    <div className={'relative'}>
      {focusTooltip && (
        <div
          className={clsx(
            'absolute opacity-0 top-[60px] left-0 duration-500 bg-tooltipBackgroundGray rounded-md z-10 text-xs py-1 px-3 text-white  ',
            {
              '!opacity-100': focus,
            },
          )}
        >
          {focusTooltip}
        </div>
      )}
      <Input
        inputClass={className}
        innerRef={inputReference}
        type={type}
        onInput={onInput}
        hasSpinner
        onChange={onChange}
        placeholder={placeholder}
        onBlur={(e) => {
          handleBlur(e);
          setIsOnBlur(true);
          validateField(name);
          removeZeroDigit(e);
          setFocus(false);
        }}
        showBlue={name in colorInputBlueState}
        showGreen={name in colorInputGreenState}
        showRed={name in colorInputRedState}
        isUpdating={isUpdating}
        disabled={disabled || !navigator.onLine}
        hidden={hidden}
        value={newVal}
        onFocus={(e) => {
          if (onFocus) onFocus(e);
          setCanCheck(true);
        }}
        onBlurCapture={onBlur}
        showError={!(meta.touched && (meta.error || showAxErros))}
        error={meta.error || axErros}
        subOrSupScriptPlaceholder={subOrSupScriptPlaceholder}
        min={props.min}
        max={props.max}
        name={props.name}
        step={props.step}
        useCurrencyMask={useCurrencyMask}
        usePhoneMask={usePhoneMask}
        useBankMask={useBankMask}
        bgClass={bgClass}
      />
    </div>
  );
}

FormikInput.defaultProps = {
  onInput: () => ({}),
  onBlur: () => ({}),
  onFocus: () => ({}),
  getError: () => ({}),
  setError: () => ({}),
  showBlue: false,
  showGreen: false,
  stepName: '',
  placeholder: '',
  disabled: false,
  hidden: false,
  useCurrencyMask: false,
  usePhoneMask: false,
  useBankMask: false,
  value: '',
  innerRef: null,
  subOrSupScriptPlaceholder: null,
  shouldAutoUpdate: false,
  updateCallback: () => ({}),
  refreshCallback: () => ({}),
  type: 'text',
  children: <></>,
};

export default FormikInput;
